import { toTypedSchema } from '@vee-validate/zod';
import { z } from '@pkg/form-builder/zod';
import { useForm } from 'vee-validate';
import { LOCALE_CODE } from '@pkg/config/languages/localeCodes';

type SignatureGeneratorResponse = {
  officeAddress?: [];
  additionalContent?: [];
  privacyPolicyText?: string;
  officePhoneNumber?: string;
  pronouns?: Array<{ title?: string }>;
};

const fetchSignatureGenerator = async(locale: LOCALE_CODE) =>
  await $fetch<SignatureGeneratorResponse>('/api/signature-generator', {
    query: { locale },
  });

function signatureGeneratorFormShape() {
  return z.object({
    country: z.string({
      required_error: 'Please select a country',
    }).min(1),
    pronoun: z.string().optional(),
    firstName: z.string({
      required_error: 'Please enter your name.',
    }).min(1),
    lastName: z.string({
      required_error: 'Please enter your last name.',
    }).min(1),
    email: z.string({
      required_error: 'Please enter your email.',
    }).email(),
    jobTitle: z.string({
      required_error: 'Please enter your job title.',
    }).min(1),
    phone: z.string().optional(),
    officePhone: z.array(z.string()).optional(),
  });
}

const parseContent = (content: []) => {
  const textsArray = content?.map((element: any) => element.children[0].text);
  return textsArray;
};

const getCMSTexts = async(locale: LOCALE_CODE) => {
  const cmsSignatureData = await fetchSignatureGenerator(locale);

  return {
    officeAddressText: parseContent(cmsSignatureData.officeAddress || []) || [],
    addressExtraContent: cmsSignatureData.additionalContent
      ? parseContent(cmsSignatureData.additionalContent as [])
      : [],
    privacyPolicyText: cmsSignatureData.privacyPolicyText || '',
    officePhoneText: cmsSignatureData.officePhoneNumber || '',
  };
};

const getCMSPronounsList = async(locale: LOCALE_CODE) => {
  const cmsSignatureData = await fetchSignatureGenerator(locale);
  return { pronouns: cmsSignatureData.pronouns ?? [] };
};

export function useSignatureGenerator() {
  const formSchema = toTypedSchema(signatureGeneratorFormShape());
  const form = useForm({
    validationSchema: formSchema,
    initialValues: {
      country: LOCALE_CODE.EN_GB,
    },
  });

  return {
    form,
    getCMSTexts,
    getCMSPronounsList,
  };
};
